const roleDict = {
    OWNER: false,
    ADMIN: false,
    PUBLISHER: false,
    ANALYZER: false,
    READONLY: false,
    'I_ADMIN&X_VIEW': false,
    'I_ADMIN&X_DENIED': false,
    'I_VIEW&X_ADMIN': false,
    'I_VIEW&X_DENIED': false,
    'I_DENIED&X_VIEW': false,
    'I_DENIED&X_ADMIN': false
} as const;

export type RoleDict = keyof typeof roleDict;

type RoleConfigList = { [K in RoleDict]: boolean };

const roleKeyDict = Object.keys(roleDict) as RoleDict[];

// Member 基础配置表
const memberRoleKeyList: RoleDict[] = [
    'ADMIN',
    'OWNER',
    'I_ADMIN&X_VIEW',
    'I_ADMIN&X_DENIED',
    'I_VIEW&X_ADMIN',
    'I_DENIED&X_ADMIN'
];

// Insight 基础配置表
const insightRoleKeyList: RoleDict[] = [
    'ANALYZER',
    'PUBLISHER',
    'ADMIN',
    'OWNER',
    'I_ADMIN&X_VIEW',
    'I_ADMIN&X_DENIED'
];

// Insight 管理角色配置表
const insightAdminRoleKeyList: RoleDict[] = [
    'ADMIN',
    'OWNER',
    'I_ADMIN&X_VIEW',
    'I_ADMIN&X_DENIED'
];

// PS 基础配置表
const pageStudioRoleKeyList: RoleDict[] = [
    'ANALYZER',
    'PUBLISHER',
    'ADMIN',
    'OWNER',
    'I_ADMIN&X_VIEW',
    'I_ADMIN&X_DENIED'
];

// PS 管理角色配置表
const pageStudioAdminRoleKeyList: RoleDict[] = [
    'PUBLISHER',
    'ADMIN',
    'OWNER',
    'I_ADMIN&X_VIEW',
    'I_ADMIN&X_DENIED'
];

// Experience 基础配置表
const experienceRoleKeyList: RoleDict[] = [
    'ANALYZER',
    'PUBLISHER',
    'ADMIN',
    'OWNER',
    'I_VIEW&X_ADMIN',
    'I_DENIED&X_ADMIN'
];

// Experience 管理角色配置表
const experienceAdminRoleKeyList: RoleDict[] = [
    'PUBLISHER',
    'ADMIN',
    'OWNER',
    'I_VIEW&X_ADMIN',
    'I_DENIED&X_ADMIN'
];

export const roleConfig = {
    member: {
        memberSetting: generateRoleConfig(memberRoleKeyList)
    },
    plan: {
        planUpgrade: generateRoleConfig(insightAdminRoleKeyList), // 套餐升级
        planChange: generateRoleConfig(['OWNER']) // 套餐变更
    },
    setting: {
        ptxCrossDomainSetting: generateRoleConfig([
            'ADMIN',
            'OWNER',
            'I_VIEW&X_ADMIN',
            'I_DENIED&X_ADMIN'
        ]),
        ptxEventSetting: generateRoleConfig(roleKeyDict), // 事件管理
        ptxEventUpdate: generateRoleConfig(experienceRoleKeyList),
        ptxEventPropertyUpdate: generateRoleConfig(experienceRoleKeyList), // 事件属性编辑
        ptxUserPropertyUpdate: generateRoleConfig(experienceRoleKeyList) // 用户属性编辑
    },
    integration: {
        integrationSetting: generateRoleConfig(['ADMIN', 'OWNER'])
    },
    insight: {
        // 是否可以进入产品
        enableInsight: generateRoleConfig([
            'OWNER',
            'ADMIN',
            'PUBLISHER',
            'ANALYZER',
            'READONLY',
            'I_ADMIN&X_VIEW',
            'I_ADMIN&X_DENIED',
            'I_VIEW&X_ADMIN',
            'I_VIEW&X_DENIED'
        ]),
        // 是否显示设置入口
        enableInsightSetting: generateRoleConfig([
            'OWNER',
            'ADMIN',
            'PUBLISHER',
            'ANALYZER',
            'I_ADMIN&X_VIEW',
            'I_ADMIN&X_DENIED'
        ]),
        adCompatibilitySetting: generateRoleConfig(insightAdminRoleKeyList),
        adGenerateURL: generateRoleConfig(insightRoleKeyList),
        channelSetting: generateRoleConfig(insightAdminRoleKeyList),
        conversionSetting: generateRoleConfig(insightRoleKeyList),
        eventSetting: generateRoleConfig(insightRoleKeyList),
        heatmapAdd: generateRoleConfig(insightRoleKeyList),
        heatmapReportSetting: generateRoleConfig(insightRoleKeyList),
        heatmapSetting: generateRoleConfig(insightRoleKeyList),
        ipExclusionSetting: generateRoleConfig(insightAdminRoleKeyList),
        pageGroupSetting: generateRoleConfig(insightRoleKeyList),
        profileAdd: generateRoleConfig(insightAdminRoleKeyList),
        profileDelete: generateRoleConfig(['OWNER']),
        profileSetting: generateRoleConfig(insightAdminRoleKeyList),
        uaExclusionSetting: generateRoleConfig(insightAdminRoleKeyList),
        urlCombinationSetting: generateRoleConfig(insightAdminRoleKeyList),
        visitSetting: generateRoleConfig(insightAdminRoleKeyList)
    },
    experience: {
        // 是否可以进入产品
        enableExperience: generateRoleConfig([
            'OWNER',
            'ADMIN',
            'PUBLISHER',
            'ANALYZER',
            'READONLY',
            'I_ADMIN&X_VIEW',
            'I_VIEW&X_ADMIN',
            'I_DENIED&X_VIEW',
            'I_DENIED&X_ADMIN'
        ]),
        enableExperienceSetting: generateRoleConfig([
            'OWNER',
            'ADMIN',
            'PUBLISHER',
            'ANALYZER',
            'I_VIEW&X_ADMIN',
            'I_DENIED&X_ADMIN'
        ]),
        experienceABTest: generateRoleConfig(experienceAdminRoleKeyList),
        experienceAdd: generateRoleConfig(experienceRoleKeyList),
        experienceCopy: generateRoleConfig(experienceRoleKeyList),
        experienceDelete: generateRoleConfig(experienceRoleKeyList),
        experienceHeatmap: generateRoleConfig(roleKeyDict),
        experiencePause: generateRoleConfig(experienceAdminRoleKeyList),
        experiencePublish: generateRoleConfig(experienceAdminRoleKeyList),
        experienceReport: generateRoleConfig(experienceRoleKeyList),
        experienceUpdate: generateRoleConfig(experienceRoleKeyList),
        goalAdd: generateRoleConfig(experienceRoleKeyList),
        goalSaveAs: generateRoleConfig(experienceRoleKeyList), // Goal-另存为新目标
        goalDelete: generateRoleConfig(experienceRoleKeyList),
        goalUpdate: generateRoleConfig(experienceRoleKeyList),
        userGroupAdd: generateRoleConfig(experienceRoleKeyList),
        userGroupCopy: generateRoleConfig(experienceRoleKeyList),
        userGroupDelete: generateRoleConfig(experienceRoleKeyList),
        userGroupExportUserList: generateRoleConfig(roleKeyDict), // User Group-导出用户列表
        userGroupSave: generateRoleConfig(experienceRoleKeyList), // User Group-保存新用户组
        userGroupSaveAs: generateRoleConfig(experienceRoleKeyList), // User Group-另存为新用户组
        userGroupUpdate: generateRoleConfig(experienceRoleKeyList),
        userGroupViewSegment: generateRoleConfig(roleKeyDict) // User Group-查看详情
    },
    pageStudio: {
        // 是否可以进入产品
        enablePageStudio: generateRoleConfig([
            'OWNER',
            'ADMIN',
            'PUBLISHER',
            'ANALYZER',
            'READONLY',
            'I_ADMIN&X_VIEW',
            'I_ADMIN&X_DENIED',
            'I_VIEW&X_ADMIN',
            'I_VIEW&X_DENIED'
        ]),
        enablePageStudioSetting: generateRoleConfig([
            'OWNER',
            'ADMIN',
            'PUBLISHER',
            'ANALYZER',
            'I_ADMIN&X_VIEW',
            'I_ADMIN&X_DENIED'
        ]),
        pageStudioAdd: generateRoleConfig(pageStudioRoleKeyList),
        pageStudioCopy: generateRoleConfig(pageStudioRoleKeyList),
        pageStudioDelete: generateRoleConfig(pageStudioRoleKeyList),
        pageStudioHeatmap: generateRoleConfig(roleKeyDict), // 全开
        pageStudioMove: generateRoleConfig(pageStudioRoleKeyList),
        pageStudioPause: generateRoleConfig(pageStudioAdminRoleKeyList),
        pageStudioPublish: generateRoleConfig(pageStudioAdminRoleKeyList),
        pageStudioReport: generateRoleConfig(roleKeyDict),
        pageStudioUpdate: generateRoleConfig(pageStudioRoleKeyList)
    }
};

/**
 * 校验角色在模块中的可执行权限
 */
export function checkModuleAvailabilityForRole<
    K1 extends keyof typeof roleConfig,
    K2 extends keyof typeof roleConfig[K1],
    K3 extends keyof typeof roleConfig[K1][K2]
>(module: K1, subModule: K2, role: K3): boolean {
    const flag = Boolean(roleConfig?.[module]?.[subModule]?.[role]);
    return flag;
}

export function checkRoleIsOwner(role?: RoleDict): boolean {
    return role?.toLocaleUpperCase() === 'OWNER';
}

export function checkRoleIsReadonly(role?: RoleDict): boolean {
    return role?.toLocaleUpperCase() === 'READONLY';
}
export function checkRoleIsAnalyzer(role?: RoleDict): boolean {
    return role?.toLocaleUpperCase() === 'ANALYZER';
}

/**
 * 生成配置
 */
function generateRoleConfig(hasAccessRoleKey: RoleDict[]): RoleConfigList {
    return roleKeyDict.reduce((acc: RoleConfigList, cur: RoleDict) => {
        acc[cur] = hasAccessRoleKey.includes(cur);
        return acc;
    }, {} as RoleConfigList);
}
